import { useId, useMemo, useState } from 'react';
import { cn } from '../cn.js';

export interface BarDatum {
  key: string;
  value: number;
  label: string;
  /** Texto do tooltip (já formatado). */
  tooltip: string;
}

/**
 * Gráfico de colunas compacto, sem dependências. Eixo Y com 3 linhas de grade,
 * tooltip ao passar o mouse e foco por teclado em cada coluna.
 */
export function BarChart({
  data,
  height = 140,
  formatAxis,
  className,
  ariaLabel,
}: {
  data: BarDatum[];
  height?: number;
  formatAxis: (v: number) => string;
  className?: string;
  ariaLabel: string;
}) {
  const [active, setActive] = useState<number | null>(null);
  const max = useMemo(() => niceMax(Math.max(0, ...data.map((d) => d.value))), [data]);
  const ticks = [max, max / 2, 0];
  const activeDatum = active === null ? null : data[active];

  return (
    <div className={cn('relative', className)}>
      <div className="flex gap-2">
        <div className="tabular flex w-12 shrink-0 flex-col justify-between text-right text-2xs text-fg-4" style={{ height }}>
          {ticks.map((t) => (
            <span key={t} className="-translate-y-1/2 first:translate-y-0 last:translate-y-0">
              {formatAxis(t)}
            </span>
          ))}
        </div>
        <div className="relative min-w-0 flex-1" style={{ height }} role="img" aria-label={ariaLabel}>
          <div className="absolute inset-0 flex flex-col justify-between" aria-hidden="true">
            {ticks.map((t) => (
              <div key={t} className="border-t border-dashed border-line first:border-solid last:border-solid" />
            ))}
          </div>
          <div className="absolute inset-0 flex items-end gap-[2px]">
            {data.map((d, i) => (
              <button
                key={d.key}
                type="button"
                aria-label={d.tooltip}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                className="group relative flex h-full min-w-0 flex-1 items-end focus:outline-none"
              >
                <span
                  className={cn(
                    'w-full rounded-t-[2px] transition-colors',
                    active === i ? 'bg-brand' : 'bg-[#b8c5f2] group-focus-visible:bg-brand',
                  )}
                  style={{ height: max > 0 ? `${Math.max(d.value > 0 ? 2 : 0, (d.value / max) * 100)}%` : 0 }}
                />
              </button>
            ))}
          </div>
          {activeDatum && active !== null && (
            <div
              className="pointer-events-none absolute -top-2 z-10 -translate-x-1/2 -translate-y-full rounded-md bg-fg px-2 py-1 text-xs whitespace-nowrap text-white"
              style={{ left: `${((active + 0.5) / data.length) * 100}%` }}
            >
              {activeDatum.tooltip}
            </div>
          )}
        </div>
      </div>
      <div className="mt-1.5 ml-14 flex justify-between text-2xs text-fg-4" aria-hidden="true">
        <span>{data[0]?.label}</span>
        <span>{data[Math.floor(data.length / 2)]?.label}</span>
        <span>{data.at(-1)?.label}</span>
      </div>
    </div>
  );
}

function niceMax(v: number) {
  if (v <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / exp;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return nice * exp;
}

/** Linha mínima para séries de saúde (CPU, memória). */
export function Sparkline({
  values,
  height = 32,
  max = 100,
  className,
  ariaLabel,
}: {
  values: number[];
  height?: number;
  max?: number;
  className?: string;
  ariaLabel: string;
}) {
  const id = useId();
  if (values.length < 2) return <div className={cn('text-xs text-fg-4', className)}>Sem série</div>;
  const w = 100;
  const pts = values.map((v, i) => [(i / (values.length - 1)) * w, height - (Math.min(v, max) / max) * (height - 2) - 1] as const);
  const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" className={cn('w-full', className)} style={{ height }} role="img" aria-label={ariaLabel}>
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="var(--color-brand)" stopOpacity="0.12" />
          <stop offset="1" stopColor="var(--color-brand)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L${w},${height} L0,${height} Z`} fill={`url(#${id})`} />
      <path d={d} fill="none" stroke="var(--color-brand)" strokeWidth="1.25" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
